import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const LOCAL_VERSION_KEY = "ilajly-app-version";
const LOCAL_BUILD_KEY = "ilajly-app-build-time";
const UPDATE_SUCCESS_KEY = "ilajly-update-success";
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

interface VersionInfo {
  version: string;
  buildTime: string;
}

interface AppVersionState {
  currentVersion: string | null;
  latestVersion: string | null;
  updateAvailable: boolean;
  updating: boolean;
  updateStage: string;
  updateProgress: number;
  updateSuccess: boolean;
  justUpdated: boolean;
  checkForUpdate: () => Promise<void>;
  performUpdate: () => Promise<void>;
  dismissUpdateSuccess: () => void;
}

export function useAppVersion(): AppVersionState {
  const [currentVersion, setCurrentVersion] = useState<string | null>(
    () => localStorage.getItem(LOCAL_VERSION_KEY)
  );
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestBuildTime, setLatestBuildTime] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [updateStage, setUpdateStage] = useState("");
  const [updateProgress, setUpdateProgress] = useState(0);
  const [justUpdated, setJustUpdated] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const checkingRef = useRef(false);

  // Check for post-update success flag on mount
  useEffect(() => {
    const flag = localStorage.getItem(UPDATE_SUCCESS_KEY);
    if (flag) {
      setJustUpdated(true);
      localStorage.removeItem(UPDATE_SUCCESS_KEY);
    }
  }, []);

  const dismissUpdateSuccess = useCallback(() => {
    setJustUpdated(false);
  }, []);

  const fetchLatestVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const latest = await fetchLatestVersion();
      if (!latest) return;

      setLatestVersion(latest.version);
      setLatestBuildTime(latest.buildTime);

      const storedVersion = localStorage.getItem(LOCAL_VERSION_KEY);
      const storedBuild = localStorage.getItem(LOCAL_BUILD_KEY);
      
      if (!storedVersion) {
        // First visit — store current version, no update needed
        localStorage.setItem(LOCAL_VERSION_KEY, latest.version);
        localStorage.setItem(LOCAL_BUILD_KEY, latest.buildTime);
        setCurrentVersion(latest.version);
        setUpdateAvailable(false);
      } else if (storedVersion !== latest.version || storedBuild !== latest.buildTime) {
        setUpdateAvailable(true);
      } else {
        setUpdateAvailable(false);
      }
    } finally {
      checkingRef.current = false;
    }
  }, [fetchLatestVersion]);

  const clearAllCaches = async () => {
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  };

  const unregisterAndReRegisterSW = async () => {
    if (!("serviceWorker" in navigator)) return;

    const swTimeout = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

    try {
      const registrations = await Promise.race([
        navigator.serviceWorker.getRegistrations(),
        swTimeout(5000).then(() => [] as readonly ServiceWorkerRegistration[]),
      ]);
      for (const reg of registrations) {
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
          await swTimeout(500);
        }
        await Promise.race([reg.update(), swTimeout(5000)]);
      }
    } catch {
      // SW update failed — continue anyway, cache is already cleared
      console.warn("Service worker update timed out, continuing...");
    }
  };

  const performUpdate = useCallback(async () => {
    setUpdating(true);
    setUpdateSuccess(false);
    try {
      // Stage 1: Checking version
      setUpdateStage("Checking version…");
      setUpdateProgress(10);
      const latest = await fetchLatestVersion();
      if (!latest) {
        setUpdateStage("Could not reach server. Try again.");
        setUpdating(false);
        return;
      }
      await new Promise((r) => setTimeout(r, 400));

      // Stage 2: Clear caches
      setUpdateStage("Clearing cache…");
      setUpdateProgress(30);
      await clearAllCaches();
      await new Promise((r) => setTimeout(r, 400));

      // Stage 3: Update service worker
      setUpdateStage("Updating service worker…");
      setUpdateProgress(55);
      await unregisterAndReRegisterSW();
      await new Promise((r) => setTimeout(r, 400));

      // Stage 4: Save new version
      setUpdateStage("Downloading update…");
      setUpdateProgress(80);
      localStorage.setItem(LOCAL_VERSION_KEY, latest.version);
      localStorage.setItem(LOCAL_BUILD_KEY, latest.buildTime);
      localStorage.setItem(UPDATE_SUCCESS_KEY, latest.version);
      await new Promise((r) => setTimeout(r, 600));

      // Stage 5: Done
      setUpdateStage("Update complete!");
      setUpdateProgress(100);
      setUpdateSuccess(true);

      // Show success for 1.5s then reload
      await new Promise((r) => setTimeout(r, 1500));

      // Force reload from server
      window.location.reload();
    } catch (err) {
      console.error("Update failed:", err);
      setUpdateStage("Update failed. Please try again.");
      setUpdating(false);
      setUpdateSuccess(false);
    }
  }, [fetchLatestVersion]);

  // Check on mount and periodically
  useEffect(() => {
    checkForUpdate();
    const interval = setInterval(checkForUpdate, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [checkForUpdate]);

  // Listen for SW updates (vite-plugin-pwa autoUpdate)
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const onControllerChange = () => {
      checkForUpdate();
    };

    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
    };
  }, [checkForUpdate]);

  // On login: check for updates and auto-apply if available
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === "SIGNED_IN") {
        const latest = await fetchLatestVersion();
        if (!latest) return;
        const storedVersion = localStorage.getItem(LOCAL_VERSION_KEY);
        const storedBuild = localStorage.getItem(LOCAL_BUILD_KEY);
        if (storedVersion && (storedVersion !== latest.version || storedBuild !== latest.buildTime)) {
          // Auto-trigger update on login
          performUpdate();
        } else if (!storedVersion) {
          // First time — just store
          localStorage.setItem(LOCAL_VERSION_KEY, latest.version);
          localStorage.setItem(LOCAL_BUILD_KEY, latest.buildTime);
          setCurrentVersion(latest.version);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [fetchLatestVersion, performUpdate]);

  return {
    currentVersion,
    latestVersion,
    updateAvailable,
    updating,
    updateStage,
    updateProgress,
    updateSuccess,
    justUpdated,
    checkForUpdate,
    performUpdate,
    dismissUpdateSuccess,
  };
}
